import classNames from 'classnames';
import SideBar from 'Components/Layouts/layout/SideBar';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  useCreateFeedbackGroupMutation,
  useDeleteFeedbackGroupMutation,
  useGetFeedbackGroupsBySurveyIdQuery,
  useUpdateFeedbackGroupTitleMutation,
} from 'store/services/feedbackGroup';
import Button from 'style-guide/Button';
import Form from 'style-guide/Form';
import { Plus, Trash } from 'style-guide/Icons';
import { TextArea } from 'style-guide/Input';
import ProgressBar from 'style-guide/ProgressBar';
import Title from 'style-guide/Typography/Title';
import LCSidebarPreview from './LCSidebarPreview';

const defaultSections = [
  {
    name: 'Series 1',
    key: 'series-1',
  },
];
const CreateSurveySideBar = ({ isPreview, setSurveyState }) => {
  const navigate = useNavigate();
  const { surveyId, seriesName: sectionName } = useParams();
  const [searchParams] = useSearchParams();
  const [updateFeedbackGroup] = useUpdateFeedbackGroupTitleMutation();
  const { data: feedbackGroupsData } = useGetFeedbackGroupsBySurveyIdQuery({ surveyId });
  const [createFeedbackGroup] = useCreateFeedbackGroupMutation();
  const [deleteFeedbackGroup] = useDeleteFeedbackGroupMutation();
  const id = searchParams.get('feedbackGroupId');
  const { control, handleSubmit } = useForm({
    mode: 'onChange',
  });
  const [sections, setSections] = useState(defaultSections);
  const defaultActiveCategory = 'introduction';
  const [activeCategory, setActiveCategory] = useState(sectionName || defaultActiveCategory);
  const addNewSeries = () => {
    const newSectionId = sections.length + 1;
    createFeedbackGroup({ surveyId, legend: `Series ${newSectionId}` });
  };
  const onRemove = (groupId) => {
    deleteFeedbackGroup({ id: groupId });
  };
  const onBlur = (idx) =>
    handleSubmit((data) => {
      const seriesName = data[`seriesName_${idx}`];
      if (seriesName) {
        updateFeedbackGroup({ legend: seriesName, id });
        navigate(`/survey/create/${seriesName}/${surveyId}`);
      }
    });
  useEffect(() => {
    if (feedbackGroupsData?.length > 0) {
      const newSections = feedbackGroupsData.map((group) => ({
        name: group.legend,
        key: group.id,
      }));
      setSections(newSections);
    }
  }, [feedbackGroupsData]);
  useEffect(() => {
    setSurveyState(feedbackGroupsData);
  }, [feedbackGroupsData, setSurveyState]);

  return isPreview ? (
    <SideBar className='mh--32'>
      <div className='progress-container mb--24' data-testid='progress-container'>
        <ProgressBar percent={25} />
      </div>
      <LCSidebarPreview />
    </SideBar>
  ) : (
    <SideBar className='mh--32'>
      <Title variant={5} className='mb--24'>
        Create survey
      </Title>
      <Button
        variant='link'
        className={classNames('filter-category d-block p--8', {
          active: activeCategory === 'introduction',
        })}
        onClick={() => {
          navigate(`/survey/create/introduction/${surveyId}`);
          setActiveCategory('introduction');
        }}
      >
        Introduction
      </Button>
      {sections?.map((section, idx) => {
        const isActive = activeCategory === section.key;
        const feedbackGroupId = feedbackGroupsData?.[idx]?.id;
        return isActive ? (
          <div key={section.key} className={classNames('filter-category d-block p--8', { active: true })}>
            <Form>
              <Controller
                name={`seriesName_${idx}`}
                control={control}
                defaultValue={section.name}
                render={({ field }) => (
                  <TextArea
                    placeholder=''
                    autoSize={{ maxRows: 5 }}
                    name={`seriesName_${idx}`}
                    autoComplete='off'
                    className='textarea'
                    {...field}
                    onBlur={() => {
                      field.onBlur();
                      onBlur(idx)();
                    }}
                  />
                )}
              />
            </Form>
          </div>
        ) : (
          <div
            key={section.key}
            className={classNames('filter-category d-flex justify-content-between p--8', { pointer: true })}
            onClick={() => {
              navigate({
                pathname: `/survey/create/${section.key}/${surveyId}`,
                search: feedbackGroupId ? `feedbackGroupId=${feedbackGroupId}` : '',
              });
              setActiveCategory(section.key);
            }}
          >
            <span>{section.name}</span>
            {sections.length > 1 && (
              <Button
                className='trash-icon pv--0'
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(section.key);
                }}
                variant='text'
                $danger
              >
                <Trash width={12} height={12} />
              </Button>
            )}
          </div>
        );
      })}
      {sections.length < 3 && (
        <Button prefix={<Plus />} variant='link' className='mt--24' onClick={addNewSeries}>
          Add New Series
        </Button>
      )}
    </SideBar>
  );
};
export default CreateSurveySideBar;
